import React, { useEffect, useState, useMemo } from 'react';
import { Calendar, Clock, User, ChevronRight, Stethoscope, Search, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import InactivityTimer from '../components/InactivityTimer';

const OpdSchedule = () => {
    const [allDepartments, setAllDepartments] = useState([]);
    const [departmentDoctors, setDepartmentDoctors] = useState({});
    const [loading, setLoading] = useState(true);

    // Filter states
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [departmentSearch, setDepartmentSearch] = useState('');
    const [doctorSearch, setDoctorSearch] = useState('');
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const getAllDepartments = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/department/deptList?branchId=${localStorage.getItem('site_id')}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });
            const departments = response.data.data;
            setAllDepartments(departments);

            // Fetch doctors for all departments in parallel using Promise.all
            const doctorPromises = departments.map(dept =>
                getDepartmentDoctors(dept.departmentId)
                    .then(doctors => ({ departmentId: dept.departmentId, doctors }))
                    .catch(error => {
                        console.error(`Error fetching doctors for department ${dept.departmentId}:`, error);
                        return { departmentId: dept.departmentId, doctors: [] };
                    })
            );

            // Wait for all API calls to complete
            const doctorsResults = await Promise.all(doctorPromises);

            // Convert array to object for easy lookup
            const doctorsData = {};
            doctorsResults.forEach(result => {
                doctorsData[result.departmentId] = result.doctors;
            });

            setDepartmentDoctors(doctorsData);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching departments:', error);
            setLoading(false);
        }
    }

    // date function with dd-mm-yyyy format
    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    }

    const getDepartmentDoctors = async (departmentId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/department/deptDocList?deptId=${departmentId}&siteId=${localStorage.getItem('site_id')}&date=${formatDate(new Date())}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching doctors for department:', error);
            return [];
        }
    }

    // Group doctors by day
    const groupDoctorsByDay = (doctors) => {
        const grouped = {};
        daysOfWeek.forEach(day => {
            grouped[day] = [];
        });

        doctors.forEach(doctor => {
            const dayName = doctor.dayName;
            if (grouped[dayName]) {
                grouped[dayName].push(doctor);
            }
        });

        return grouped;
    };

    // Get all unique doctors from all departments
    const allDoctors = useMemo(() => {
        const doctorsSet = new Map();
        Object.values(departmentDoctors).forEach(doctors => {
            doctors.forEach(doctor => {
                if (!doctorsSet.has(doctor.empId)) {
                    doctorsSet.set(doctor.empId, doctor);
                }
            });
        });
        return Array.from(doctorsSet.values());
    }, [departmentDoctors]);

    // Filter departments based on search
    const filteredDepartments = useMemo(() => {
        if (!departmentSearch) return allDepartments;
        return allDepartments.filter(dept =>
            (dept.deptName || dept.departmentName || '').toLowerCase().includes(departmentSearch.toLowerCase())
        );
    }, [allDepartments, departmentSearch]);

    // Filter doctors based on search and selected department
    const filteredDoctors = useMemo(() => {
        let doctors = allDoctors;

        // If department is selected, show only doctors from that department
        if (selectedDepartment) {
            doctors = departmentDoctors[selectedDepartment] || [];
        }

        // Apply search filter
        if (doctorSearch) {
            doctors = doctors.filter(doctor =>
                (doctor.empName || '').toLowerCase().includes(doctorSearch.toLowerCase())
            );
        }

        return doctors;
    }, [allDoctors, selectedDepartment, doctorSearch, departmentDoctors]);

    // Filtered departments to display
    const displayDepartments = useMemo(() => {
        if (!selectedDepartment && !selectedDoctor) {
            return allDepartments;
        }

        if (selectedDepartment) {
            return allDepartments.filter(dept => String(dept.departmentId) === String(selectedDepartment));
        }

        if (selectedDoctor) {
            // Find departments that have this doctor
            const deptIds = [];
            Object.keys(departmentDoctors).forEach(deptId => {
                const hasDr = departmentDoctors[deptId].some(doc => String(doc.empId) === String(selectedDoctor));
                if (hasDr) deptIds.push(deptId);
            });
            return allDepartments.filter(dept => deptIds.includes(String(dept.departmentId)));
        }

        return allDepartments;
    }, [selectedDepartment, selectedDoctor, allDepartments, departmentDoctors]);

    // Handle department selection
    const handleDepartmentSelect = (deptId) => {
        setSelectedDepartment(deptId);
        setSelectedDoctor(''); // Reset doctor selection
        setDepartmentSearch('');
        setShowDepartmentDropdown(false);
    };

    // Handle doctor selection
    const handleDoctorSelect = (empId) => {
        setSelectedDoctor(empId);
        setDoctorSearch('');
        setShowDoctorDropdown(false);
    };

    // Clear all filters
    const clearFilters = () => {
        setSelectedDepartment('');
        setSelectedDoctor('');
        setDepartmentSearch('');
        setDoctorSearch('');
    };

    // Get selected department name
    // Get selected department name
    const getSelectedDepartmentName = () => {
        const dept = allDepartments.find(d => String(d.departmentId) === String(selectedDepartment));
        return dept ? (dept.deptName || dept.departmentName) : '';
    };

    // Get selected doctor name
    const getSelectedDoctorName = () => {
        const doctor = allDoctors.find(d => String(d.empId) === String(selectedDoctor));
        return doctor ? doctor.empName : '';
    };

    useEffect(() => {
        getAllDepartments();
    }, []);

    return (
        <>
            <InactivityTimer />
            <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen">
                {/* Hero Section */}
                <div className="bg-blue-700 text-white py-8 px-4 shadow-lg">
                    <div className="max-w-6xl mx-auto text-center">
                        <Link
                            to="/"
                            className="absolute left-4 top-4 flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-semibold py-2 px-4 rounded-full shadow transition"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">KIMS HOSPITAL</h1>
                        <div className="w-24 h-1 bg-yellow-400 mx-auto mb-4"></div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-6xl mx-auto px-4 py-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-gray-800">
                        OPD Calendar
                    </h2>

                    {/* Filter Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                        <div className="flex items-center mb-4">
                            <Filter className="text-blue-700 mr-2" size={24} />
                            <h3 className="text-xl font-bold text-gray-800">Filter Calendar</h3>
                            {(selectedDepartment || selectedDoctor) && (
                                <button
                                    onClick={clearFilters}
                                    className="ml-auto flex items-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg transition font-semibold"
                                >
                                    <X size={18} />
                                    Clear Filters
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Department Dropdown */}
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Select Department
                                </label>
                                <div className="relative">
                                    <div
                                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus-within:border-blue-500 cursor-pointer bg-white"
                                        onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center flex-1">
                                                <Search className="text-gray-400 mr-2" size={20} />
                                                {selectedDepartment ? (
                                                    <span className="text-gray-800 font-medium">{getSelectedDepartmentName()}</span>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        placeholder="Search department..."
                                                        value={departmentSearch}
                                                        onChange={(e) => {
                                                            setDepartmentSearch(e.target.value);
                                                            setShowDepartmentDropdown(true);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="flex-1 outline-none bg-transparent"
                                                    />
                                                )}
                                            </div>
                                            {selectedDepartment && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedDepartment('');
                                                        setDepartmentSearch('');
                                                    }}
                                                    className="ml-2 text-gray-400 hover:text-red-500"
                                                >
                                                    <X size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Department Dropdown Menu */}
                                    {showDepartmentDropdown && (
                                        <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                                            {filteredDepartments.length > 0 ? (
                                                filteredDepartments.map((dept) => (
                                                    <div
                                                        key={dept.departmentId}
                                                        onClick={() => handleDepartmentSelect(dept.departmentId)}
                                                        className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition ${String(selectedDepartment) === String(dept.departmentId) ? 'bg-blue-100' : ''
                                                            }`}
                                                    >
                                                        <div className="font-medium text-gray-800">
                                                            {dept.deptName || dept.departmentName}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-4 py-3 text-gray-500 text-center">
                                                    No departments found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Doctor Dropdown */}
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Select Doctor
                                </label>
                                <div className="relative">
                                    <div
                                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus-within:border-blue-500 cursor-pointer bg-white"
                                        onClick={() => setShowDoctorDropdown(!showDoctorDropdown)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center flex-1">
                                                <Search className="text-gray-400 mr-2" size={20} />
                                                {selectedDoctor ? (
                                                    <span className="text-gray-800 font-medium">{getSelectedDoctorName()}</span>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        placeholder="Search doctor..."
                                                        value={doctorSearch}
                                                        onChange={(e) => {
                                                            setDoctorSearch(e.target.value);
                                                            setShowDoctorDropdown(true);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="flex-1 outline-none bg-transparent"
                                                    />
                                                )}
                                            </div>
                                            {selectedDoctor && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedDoctor('');
                                                        setDoctorSearch('');
                                                    }}
                                                    className="ml-2 text-gray-400 hover:text-red-500"
                                                >
                                                    <X size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Doctor Dropdown Menu */}
                                    {showDoctorDropdown && (
                                        <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                                            {filteredDoctors.length > 0 ? (
                                                filteredDoctors.map((doctor) => (
                                                    <div
                                                        key={doctor.empId}
                                                        onClick={() => handleDoctorSelect(doctor.empId)}
                                                        className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition ${String(selectedDoctor) === String(doctor.empId) ? 'bg-blue-100' : ''
                                                            }`}
                                                    >
                                                        <div className="font-medium text-gray-800">
                                                            {doctor.empName}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            Emp No: {doctor.empNo}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-4 py-3 text-gray-500 text-center">
                                                    No doctors found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active Filters Display */}
                        {(selectedDepartment || selectedDoctor) && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center flex-wrap gap-2">
                                    <span className="text-sm font-semibold text-gray-600">Active Filters:</span>
                                    {selectedDepartment && (
                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                                            {getSelectedDepartmentName()}
                                            <X
                                                size={14}
                                                className="cursor-pointer hover:text-red-600"
                                                onClick={() => setSelectedDepartment('')}
                                            />
                                        </span>
                                    )}
                                    {selectedDoctor && (
                                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                                            {getSelectedDoctorName()}
                                            <X
                                                size={14}
                                                className="cursor-pointer hover:text-red-600"
                                                onClick={() => setSelectedDoctor('')}
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center py-10">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
                            <p className="mt-4 text-gray-600">Loading departments and doctors...</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {displayDepartments.map((department) => {
                                const doctors = departmentDoctors[department.departmentId] || [];

                                // Filter doctors if a specific doctor is selected
                                const filteredDoctorsList = selectedDoctor
                                    ? doctors.filter(doc => String(doc.empId) === String(selectedDoctor))
                                    : doctors;

                                const doctorsByDay = groupDoctorsByDay(filteredDoctorsList);

                                // Skip if no doctors after filtering
                                if (filteredDoctorsList.length === 0) {
                                    return null;
                                }

                                return (
                                    <div key={department.departmentId || department.deptId} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-blue-100">
                                        {/* Department Header */}
                                        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-4">
                                                    <div className="bg-white text-blue-700 p-3 rounded-full shadow-lg">
                                                        <Stethoscope size={28} />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-2xl md:text-3xl font-bold">{department.deptName || department.departmentName}</h3>
                                                        {/* <p className="text-blue-100 mt-1">Department Schedule</p> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Days and Doctors Schedule */}
                                        <div className="p-6">
                                            {doctors.length > 0 ? (
                                                <div className="space-y-6">
                                                    {daysOfWeek.map((day) => {
                                                        const dayDoctors = doctorsByDay[day];

                                                        if (!dayDoctors || dayDoctors.length === 0) {
                                                            return null; // Skip days with no doctors
                                                        }

                                                        return (
                                                            <div key={day} className="border border-gray-200 rounded-lg overflow-hidden">
                                                                {/* Day Header */}
                                                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b-2 border-blue-200">
                                                                    <div className="flex items-center space-x-2">
                                                                        <Calendar size={20} className="text-blue-700" />
                                                                        <h4 className="text-lg font-bold text-blue-800">{day}</h4>
                                                                        <span className="ml-auto bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                                                            {dayDoctors.length} {dayDoctors.length === 1 ? 'Doctor' : 'Doctors'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Doctors List for this Day */}
                                                                <div className="divide-y divide-gray-100">
                                                                    {dayDoctors.map((doctor, index) => (
                                                                        <div
                                                                            key={`${doctor.empId}-${index}`}
                                                                            className="p-4 hover:bg-blue-50 transition-colors duration-200"
                                                                        >
                                                                            <div className="flex items-start justify-between">
                                                                                <div className="flex items-start space-x-3 flex-1">
                                                                                    <div className="bg-blue-100 text-blue-700 p-2 rounded-full mt-1">
                                                                                        <User size={20} />
                                                                                    </div>
                                                                                    <div className="flex-1">
                                                                                        <h5 className="text-lg font-bold text-gray-800">
                                                                                            {doctor.empName || 'N/A'}
                                                                                        </h5>
                                                                                        <div className="mt-2 space-y-1">
                                                                                            <p className="text-sm text-gray-600 flex items-center">
                                                                                                <span className="font-semibold mr-2">Employee No:</span>
                                                                                                {doctor.empNo || 'N/A'}
                                                                                            </p>
                                                                                            {/* <p className="text-sm text-gray-600 flex items-center">
                                                                                                <span className="font-semibold mr-2">Service:</span>
                                                                                                {doctor.serviceName || 'N/A'}
                                                                                            </p> */}
                                                                                            {doctor.totalAmount > 0 && (
                                                                                                <p className="text-sm text-green-600 font-semibold flex items-center">
                                                                                                    <span className="mr-2">Fee:</span>
                                                                                                    ₹{doctor.totalAmount}
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="ml-4">
                                                                                    <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                                                                                        Available
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12">
                                                    <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                                                        <User size={40} className="text-gray-400" />
                                                    </div>
                                                    <p className="text-gray-500 text-lg">No doctors available for this department</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* No Results Message */}
                            {displayDepartments.length === 0 && (
                                <div className="text-center py-16 bg-white rounded-xl shadow-lg">
                                    <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                                        <Search size={48} className="text-gray-400" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Results Found</h3>
                                    <p className="text-gray-500 mb-6">Try adjusting your filters to see more results</p>
                                    <button
                                        onClick={clearFilters}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
                                    >
                                        Clear All Filters
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default OpdSchedule;